import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Tooltip,
  Dropdown,
  MenuProps,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  MoreOutlined,
  FileTextOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { Bill, BillSearchFilters } from "../../types/Billing";
import BillDetailsModal from "./BillDetailsModal";
import UpdatePaymentModal from "./UpdatePaymentModal";
import SaleReturnModal from "./SaleReturnModal";
import dayjs from "dayjs";
import {
  fetchBillsStart,
  deleteBillStart,
  updateBillStart,
} from "../../store/slices/billingSlice";
import { getStockItemsStart } from "../../store/slices/stockSlice";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface BillsListProps {
  onCreateNew: () => void;
}

const BillsList: React.FC<BillsListProps> = ({ onCreateNew }) => {
  const dispatch = useDispatch();
  const { bills, loading } = useSelector((state: RootState) => state.billing);
  const [filters, setFilters] = useState<BillSearchFilters>({});
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [returnModalVisible, setReturnModalVisible] = useState(false);

  useEffect(() => {
    dispatch(fetchBillsStart(filters));
  }, [dispatch, filters]);

  const handleSearch = (value: string) => {
    setFilters({ ...filters, search: value || undefined });
  };

  const handleStatusFilter = (status: string) => {
    setFilters({
      ...filters,
      paymentStatus: status === "all" ? undefined : (status as any),
    });
  };

  const handleDateRangeFilter = (dates: any) => {
    if (dates) {
      setFilters({
        ...filters,
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      setFilters({
        ...filters,
        startDate: undefined,
        endDate: undefined,
      });
    }
  };

  const handleViewDetails = (bill: Bill) => {
    setSelectedBill(bill);
    setDetailsModalVisible(true);
  };

  const handleUpdatePayment = (bill: Bill) => {
    setSelectedBill(bill);
    setPaymentModalVisible(true);
  };

  const handleReturn = (bill: Bill) => {
    setSelectedBill(bill);
    setReturnModalVisible(true);
  };

  const handleDeleteBill = (billId: string) => {
    Modal.confirm({
      title: "Delete Bill",
      content:
        "Are you sure you want to delete this bill? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      onOk: () => {
        dispatch(deleteBillStart(billId));
      },
    });
  };

  const handlePrintBill = (bill: Bill) => {
    // Open bill in new window for printing
    const printWindow = window.open(`/billing/${bill._id}/print`, "_blank");
    if (printWindow) {
      printWindow.focus();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "success";
      case "partially_paid":
        return "warning";
      case "pending":
        return "default";
      case "refunded":
        return "processing";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "paid":
        return "Paid";
      case "partially_paid":
        return "Partially Paid";
      case "pending":
        return "Pending";
      case "refunded":
        return "Refunded";
      default:
        return status;
    }
  };

  const getActionMenuItems = (bill: Bill): MenuProps["items"] => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View Details",
      onClick: () => handleViewDetails(bill),
    },
    {
      key: "return",
      icon: <RollbackOutlined />,
      label: "Return Medicine",
      onClick: () => handleReturn(bill),
    },
    {
      key: "payment",
      icon: <EditOutlined />,
      label: "Update Payment",
      onClick: () => handleUpdatePayment(bill),
    },
    {
      key: "print",
      icon: <PrinterOutlined />,
      label: "Print Bill",
      onClick: () => handlePrintBill(bill),
    },
    {
      type: "divider",
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete Bill",
      danger: true,
      onClick: () => handleDeleteBill(bill._id),
    },
  ];

  const columns = [
    {
      title: "Bill Number",
      dataIndex: "billNumber",
      key: "billNumber",
      render: (text: string, record: Bill) => (
        <Button
          type="link"
          onClick={() => handleViewDetails(record)}
          style={{ padding: 0, height: "auto" }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_: any, record: Bill) => {
        const patient =
          typeof record.patient === "object" && record.patient
            ? record.patient
            : null;
        const name = patient?.name || record.customer?.name || "Walk-in";
        const phone = patient?.phone || record.customer?.phone || "";
        const id =
          patient && "patientId" in patient ? patient.patientId : undefined;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{name}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {id ? `${id} • ` : ""}
              {phone || (patient ? "" : "Walk-in")}
            </div>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("DD MMM YYYY"),
      sorter: (a: Bill, b: Bill) =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: "Amount",
      dataIndex: "grandTotal",
      key: "grandTotal",
      render: (amount: number) => `₹${(amount || 0).toFixed(2)}`,
      sorter: (a: Bill, b: Bill) => (a.grandTotal || 0) - (b.grandTotal || 0),
    },
    {
      title: "Paid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (amount: number) => `₹${(amount || 0).toFixed(2)}`,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (balance: number) => (
        <span style={{ color: (balance || 0) > 0 ? "#ff4d4f" : "#52c41a" }}>
          ₹{(balance || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (method: string) => (
        <Tag>
          {method ? method.charAt(0).toUpperCase() + method.slice(1) : "N/A"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (text: any, record: Bill) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Return Medicine">
            <Button
              type="text"
              icon={<RollbackOutlined />}
              onClick={() => handleReturn(record)}
            />
          </Tooltip>
          <Tooltip title="Update Payment">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleUpdatePayment(record)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-panel">
        <div className="dense-toolbar">
          <Row gutter={[8, 8]} align="middle" style={{ width: "100%" }}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search patient name or ID"
                prefix={<SearchOutlined />}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
                size="small"
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Status"
                style={{ width: "100%" }}
                onChange={handleStatusFilter}
                allowClear
                size="small"
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="partially_paid">Partially Paid</Option>
                <Option value="paid">Paid</Option>
                <Option value="refunded">Refunded</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                style={{ width: "100%" }}
                onChange={handleDateRangeFilter}
                format="DD MMM YYYY"
                size="small"
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={onCreateNew}
                style={{ width: "100%" }}
                size="small"
              >
                New Bill
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          className="dense-table"
          size="small"
          columns={columns}
          dataSource={Array.isArray(bills) ? bills : []}
          rowKey="_id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            size: "small",
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} bills`,
          }}
          scroll={{ x: 1000 }}
        />
      </div>

      {selectedBill && (
        <>
          <BillDetailsModal
            bill={selectedBill}
            visible={detailsModalVisible}
            onClose={() => {
              setDetailsModalVisible(false);
              if (!paymentModalVisible && !returnModalVisible) {
                setSelectedBill(null);
              }
            }}
          />

          <UpdatePaymentModal
            bill={selectedBill}
            visible={paymentModalVisible}
            onClose={() => {
              setPaymentModalVisible(false);
              if (!detailsModalVisible && !returnModalVisible) {
                setSelectedBill(null);
              }
            }}
            onUpdate={(billId, updateData) => {
              dispatch(updateBillStart({ id: billId, billData: updateData }));
              setPaymentModalVisible(false);
              setSelectedBill(null);
            }}
          />

          <SaleReturnModal
            bill={selectedBill}
            visible={returnModalVisible}
            onClose={() => {
              setReturnModalVisible(false);
              if (!detailsModalVisible && !paymentModalVisible) {
                setSelectedBill(null);
              }
            }}
            onSuccess={() => {
              dispatch(fetchBillsStart(filters));
              dispatch(getStockItemsStart({ page: 1, limit: 500 }));
            }}
          />
        </>
      )}
    </div>
  );
};

export default BillsList;
